import * as Sentry from '@sentry/react'

// Sentry configuration
export const initSentry = () => {
  // Only initialize Sentry in production or if explicitly enabled
  if (import.meta.env.PROD || import.meta.env.VITE_ENABLE_SENTRY === 'true') {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.VITE_APP_ENV || import.meta.env.MODE,
      
      // Performance monitoring
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in prod, 100% in dev
      
      // Session replay
      replaysSessionSampleRate: 0.1, // 10% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
      
      // Additional configuration
      beforeSend: (event) => {
        // Filter out development errors in production
        if (import.meta.env.PROD && event.environment === 'development') {
          return null
        }
        return event
      },
      
      // Release tracking
      release: import.meta.env.VITE_APP_VERSION || '1.0.0',
      
      // Debug mode for development
      debug: !import.meta.env.PROD,
      
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
    })
  }
}

// Error boundary fallback component
export const SentryErrorFallback = ({ error, resetError }: { error: Error; resetError: () => void }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md mx-auto text-center p-6">
        <div className="text-red-500 text-6xl mb-4">⚠️</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h1>
        <p className="text-gray-600 mb-4">
          We're sorry! An unexpected error occurred. Our team has been notified.
        </p>
        <div className="space-y-2">
          <button
            onClick={resetError}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
          >
            Try again
          </button>
          <button
            onClick={() => window.location.href = '/'}
            className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 transition-colors"
          >
            Go to home
          </button>
        </div>
        {!import.meta.env.PROD && (
          <details className="mt-4 text-left">
            <summary className="cursor-pointer text-sm text-gray-500">Error details (dev only)</summary>
            <pre className="text-xs text-red-600 mt-2 p-2 bg-red-50 rounded overflow-auto">
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  )
}

// Higher-order component for capturing user context
export const withSentryUser = (userId?: string, email?: string, username?: string) => {
  Sentry.setUser({
    id: userId,
    email: email,
    username: username,
  })
}

// Custom error reporting
export const reportError = (error: Error, context?: Record<string, any>) => {
  Sentry.captureException(error, {
    extra: context,
  })
}

// Performance monitoring helpers
export const startSpan = (name: string, op: string, callback: () => void) => {
  return Sentry.startSpan({ name, op }, callback)
}

export const addBreadcrumb = (message: string, category?: string, level?: Sentry.SeverityLevel) => {
  Sentry.addBreadcrumb({
    message,
    category: category || 'app',
    level: level || 'info',
    timestamp: Date.now() / 1000,
  })
} 